import fs from 'fs-extra';
import path from 'path';
import { parse } from '@babel/parser';
import traverse from '@babel/traverse';
import generate from '@babel/generator';
import * as t from '@babel/types';

export interface TailwindConfigUpdaterOptions {
  projectRoot: string;
  force?: boolean;
}

const VIBESHIP_PATHS = [
  './node_modules/@vibeship/ui/dist/**/*.{js,mjs}',
  './node_modules/@vibeship/devtools/dist/**/*.{js,mjs}',
];

export class TailwindConfigUpdater {
  private configPath: string | null = null;
  private isTypeScript: boolean = false;

  constructor(private options: TailwindConfigUpdaterOptions) {}

  async update(): Promise<boolean> {
    try {
      // Find the Tailwind config file
      this.configPath = await this.findConfigFile();
      if (!this.configPath) {
        console.log('⚠️  No Tailwind config found. Skipping Tailwind setup...');
        return false;
      }

      // Read the config file
      const configContent = await fs.readFile(this.configPath, 'utf-8');
      
      // Parse and update the config
      const updatedContent = await this.updateConfigContent(configContent);
      
      // Write back the updated config
      await fs.writeFile(this.configPath, updatedContent);
      
      console.log('✅ Updated Tailwind config to include Vibeship packages');
      return true;
    } catch (error) {
      console.error('❌ Failed to update Tailwind config:', error);
      return false;
    }
  }

  private async findConfigFile(): Promise<string | null> {
    const possibleFiles = [
      'tailwind.config.ts',
      'tailwind.config.js',
      'tailwind.config.mjs',
      'tailwind.config.cjs',
    ];

    for (const file of possibleFiles) {
      const filePath = path.join(this.options.projectRoot, file);
      if (await fs.pathExists(filePath)) {
        this.isTypeScript = file.endsWith('.ts');
        return filePath;
      }
    }

    return null;
  }

  private async updateConfigContent(content: string): Promise<string> {
    // Parse the config file
    const ast = parse(content, {
      sourceType: 'module',
      plugins: this.isTypeScript ? ['typescript'] : [],
    });

    let modified = false;

    // Traverse the AST to find the content array
    traverse(ast, {
      ObjectProperty(path) {
        // Look for the 'content' property
        if (
          t.isIdentifier(path.node.key, { name: 'content' }) &&
          t.isArrayExpression(path.node.value)
        ) {
          const contentArray = path.node.value;
          const existingPaths = contentArray.elements
            .filter((el): el is t.StringLiteral => t.isStringLiteral(el))
            .map(el => el.value);

          // Check if our paths are already included
          const pathsToAdd = VIBESHIP_PATHS.filter(
            vibeshipPath => !existingPaths.some(existing => 
              existing.includes('@vibeship/ui') || existing.includes('@vibeship/devtools')
            )
          );

          if (pathsToAdd.length > 0) {
            // Add our paths to the content array
            pathsToAdd.forEach(pathToAdd => {
              contentArray.elements.push(t.stringLiteral(pathToAdd));
            });
            modified = true;
          }
        }
      },
    });

    if (!modified) {
      console.log('ℹ️  Tailwind config already includes Vibeship packages');
      return content;
    }

    // Generate the updated code
    const { code } = generate(ast, {
      retainLines: true,
      retainFunctionParens: true,
      compact: false,
    });

    return code;
  }

  static async updateTailwindConfig(options: TailwindConfigUpdaterOptions): Promise<boolean> {
    const updater = new TailwindConfigUpdater(options);
    return updater.update();
  }
}